import React, { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../../context/AppContext";
import Pagination from "../../../../../components/Pagination";
import CardLoader from "../../../../../components/CardLoader";
import Transition from "../../../../../components/Transition";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import { AnimatePresence } from "framer-motion";
import { AppAnnouncementsData } from "../../../../../network/api/AppAnnouncementsData";
import { AppAnnouncementData } from "../../../network/api/AppAnnouncementData";
import { AnnouncementMessage } from "../../../components/AppAnnouncements";

const parseDate = (value) => {
    if (!value) {
        return null;
    }

    const normalizedValue = typeof value === "string" ? value.replace(" ", "T") : value;
    const parsed = new Date(normalizedValue);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateLabel = (value) => {
    const parsed = parseDate(value);

    if (!parsed) {
        return "Not scheduled";
    }

    return parsed.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const isCurrentlyVisible = (announcement) => {
    const now = new Date();
    const startsAt = parseDate(announcement?.starts_at);
    const endsAt = parseDate(announcement?.ends_at);
    const isActive = String(announcement?.is_active) === "1";

    if (!isActive) {
        return false;
    }

    if (startsAt && startsAt > now) {
        return false;
    }

    if (endsAt && endsAt < now) {
        return false;
    }

    return true;
};

export default function AnnouncementList({
    setEditMode,
    setSelectedAnnouncement,
    selectedAnnouncement,
    canManageAnnouncements = false,
}) {
    const { appData } = useAppContext();
    const [announcementsList, setAnnouncementsList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [reloadKey, setReloadKey] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
        visible: false,
    });
    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 10,
        visiblePages: [],
    });
    const [pageFilters, setPageFilters] = useState({
        page: 1,
        limit: 10,
        type: null,
        active_only: false,
    });

    const activeCount = useMemo(
        () => announcementsList.filter((announcement) => isCurrentlyVisible(announcement)).length,
        [announcementsList]
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {
            setPageProps((prev) => ({ ...prev, page }));
            setPageFilters((prev) => ({ ...prev, page }));
        }
    };

    const handleLimitChange = (value) => {
        setPageFilters((prev) => ({
            ...prev,
            page: 1,
            limit: value,
        }));
    };

    const handleTypeChange = (value) => {
        setPageFilters((prev) => ({
            ...prev,
            page: 1,
            type: value || null,
        }));
    };

    const handleVisibilityFilterChange = (value) => {
        setPageFilters((prev) => ({
            ...prev,
            page: 1,
            active_only: value === "active",
        }));
    };

    const handleEditSwitch = (event, announcement = null) => {
        event.preventDefault();
        setSelectedAnnouncement(announcement);
        setEditMode(true);
    };

    const handleDeleteAnnouncement = async () => {
        if (!canManageAnnouncements) {
            setToastConfig({
                message: "Trusted super admin access is required to delete announcements.",
                type: "danger",
                visible: true,
            });
            return;
        }

        setIsLoading(true);

        const response = await AppAnnouncementData({
            method: "DELETE",
            id: selectedAnnouncement?.id,
        });

        setIsLoading(false);
        setConfirmDelete(false);

        if (response.status === "success") {
            document.querySelector("#deleteAnnouncementModal .btn-close")?.click();
            setAnnouncementsList((prev) => prev.filter((item) => item.id !== selectedAnnouncement?.id));
            setToastConfig({
                message: response.message || "Announcement deleted successfully.",
                type: "success",
                visible: true,
            });
            setReloadKey((prev) => !prev);
        } else {
            setToastConfig({
                message: response.message || "Unable to delete announcement.",
                type: "danger",
                visible: true,
            });
        }
    };

    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (!appData?.id) {
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);

            const response = await AppAnnouncementsData({
                appId: appData.id,
                pageFilters,
            });

            if (response.status === "success" && response.data) {
                setAnnouncementsList(Array.isArray(response.data.data) ? response.data.data : []);
                setPageProps({
                    page: response.data.page,
                    totalPages: response.data.total_pages,
                    totalEntries: response.data.total_records,
                    entryLimit: response.data.limit,
                    visiblePages: response.data.visible_pages,
                });
            } else {
                setErrorMessage({
                    code: response.code,
                    message: response.message || "Failed to load announcements.",
                });
            }

            setIsLoading(false);
        };

        fetchAnnouncements();
    }, [appData?.id, pageFilters, reloadKey]);

    return (
        <>
            <AnimatePresence mode="wait">
                <Transition key="announcement-list-page">
                    <Toast
                        message={toastConfig.message}
                        type={toastConfig.type}
                        visible={toastConfig.visible}
                        setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                    />

                    <div className="card custom-card">
                        {errorMessage && (
                            <CardLoader errorMessage={errorMessage} onRetry={() => setReloadKey((prev) => !prev)} />
                        )}

                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                                <div>
                                    <h4 className="card-title mb-1">Announcements</h4>
                                    <p className="text-muted mb-0 small">{activeCount} currently visible on the app</p>
                                </div>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={(event) => handleEditSwitch(event)}
                                    disabled={!canManageAnnouncements}
                                >
                                    <i className="fa fa-plus"></i> Add
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            {!canManageAnnouncements && (
                                <div className="alert alert-warning alert-alt solid">
                                    You can review announcements here, but only trusted super admins can create, update, or delete them.
                                </div>
                            )}

                            <div className="table-responsive dataTables_wrapper no-footer">
                                <div className="dataTables_length me-4" id="page_limit">
                                    <label>
                                        Show{" "}
                                        <select
                                            name="page_limit"
                                            aria-controls="announcement_table"
                                            className=""
                                            value={pageProps.entryLimit}
                                            onChange={(event) => handleLimitChange(Number(event.target.value))}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>{" "}
                                        entries
                                    </label>
                                </div>

                                <div className="dataTables_length me-4" id="announcement_visibility_filter">
                                    <label>
                                        Visibility{" "}
                                        <select
                                            name="visibility"
                                            aria-controls="announcement_table"
                                            className=""
                                            value={String(pageFilters.active_only ? "active" : "all")}
                                            onChange={(event) => handleVisibilityFilterChange(event.target.value)}
                                        >
                                            <option value="all">all</option>
                                            <option value="active">active only</option>
                                        </select>
                                    </label>
                                </div>

                                <div className="dataTables_length" id="announcement_type_filter">
                                    <label>
                                        Type{" "}
                                        <select
                                            name="type"
                                            aria-controls="announcement_table"
                                            className=""
                                            value={pageFilters.type || ""}
                                            onChange={(event) => handleTypeChange(event.target.value)}
                                        >
                                            <option value="">all</option>
                                            <option value="info">info</option>
                                            <option value="success">success</option>
                                            <option value="warning">warning</option>
                                            <option value="error">error</option>
                                        </select>
                                    </label>
                                </div>

                                <div id="announcement_filter" className="dataTables_filter">
                                    <label>
                                        Search:
                                        <input
                                            type="search"
                                            className=""
                                            placeholder="Announcements"
                                            aria-controls="announcement_table"
                                            disabled
                                        />
                                    </label>
                                </div>

                                <table className="table table-striped" style={{ minWidth: "1140px" }}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Title</th>
                                            <th>Type</th>
                                            <th>Delivery</th>
                                            <th>Status</th>
                                            <th>Starts</th>
                                            <th>Ends</th>
                                            <th>Updated</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {announcementsList.map((announcement, index) => (
                                            <tr key={announcement.id || index}>
                                                <td>{(pageProps.entryLimit * (pageProps.page - 1)) + (index + 1)}</td>
                                                <td>
                                                    <strong>{announcement.title}</strong>
                                                    <div className="small text-muted text-truncate" style={{ maxWidth: "280px" }}>
                                                        <AnnouncementMessage message={announcement.message} className="text-reset text-decoration-underline" />
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${announcement.type === "error" ? "danger" : announcement.type}`}>
                                                        {announcement.type}
                                                    </span>
                                                </td>
                                                <td>
                                                    {String(announcement?.send_to_trading_alerts) === "1" || announcement?.send_to_trading_alerts === true ? (
                                                        <span className="badge bg-info text-dark">trading_alerts</span>
                                                    ) : (
                                                        <span className="badge bg-light text-dark">Manual only</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isCurrentlyVisible(announcement) ? (
                                                        <span className="badge bg-success">Visible</span>
                                                    ) : String(announcement?.is_active) === "1" ? (
                                                        <span className="badge bg-warning text-dark">Scheduled</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">Inactive</span>
                                                    )}
                                                </td>
                                                <td>{formatDateLabel(announcement.starts_at)}</td>
                                                <td>{formatDateLabel(announcement.ends_at)}</td>
                                                <td>{formatDateLabel(announcement.updated)}</td>
                                                <td>
                                                    <div className="dropdown text-sans-serif">
                                                        <button className="btn btn-primary tp-btn-light sharp" type="button" data-bs-toggle="dropdown">
                                                            <span><i className="fa fa-ellipsis-v"></i></span>
                                                        </button>
                                                        <div className="dropdown-menu dropdown-menu-end border py-0">
                                                            <div className="py-2">
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={(event) => handleEditSwitch(event, announcement)}
                                                                    disabled={!canManageAnnouncements}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="dropdown-item text-danger"
                                                                    onClick={() => setSelectedAnnouncement(announcement)}
                                                                    data-bs-target="#deleteAnnouncementModal"
                                                                    data-bs-toggle="modal"
                                                                    disabled={!canManageAnnouncements}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <Pagination pageProps={pageProps} onPageChange={handlePageChange} />
                            </div>
                        </div>
                    </div>
                </Transition>
            </AnimatePresence>

            <div className="modal fade" id="deleteAnnouncementModal">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Announcement</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">
                            <h6>Are you sure you want to delete <strong>{selectedAnnouncement?.title}</strong>?</h6>
                            <p>This action will remove the announcement from the app and the admin list.</p>
                            <div className="form-check mt-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="confirmDeleteAnnouncementCheck"
                                    checked={confirmDelete}
                                    onChange={(event) => setConfirmDelete(event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="confirmDeleteAnnouncementCheck">
                                    I understand the consequences of this action.
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-dark light" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                disabled={!confirmDelete || isLoading || !canManageAnnouncements}
                                onClick={handleDeleteAnnouncement}
                            >
                                {isLoading ? <ButtonLoader /> : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
